"use client";

import { useEffect } from "react";
import { decodeJwtClaims, extractRoles, extractTenantIds } from "./claims";
import { useAuthStore, type SessionState } from "./auth-store";
import { useClarityAuth } from "./use-clarity-auth";

const loadingState: SessionState = {
  status: "loading",
  accessToken: null,
  claims: null,
  tenantIds: [],
  roles: [],
  error: null,
};

const signedOutState: SessionState = {
  status: "signed_out",
  accessToken: null,
  claims: null,
  tenantIds: [],
  roles: [],
  error: null,
};

export function useInitializeSession() {
  const { getAccessTokenSilently, isAuthenticated, isLoading } =
    useClarityAuth();
  const setSessionState = useAuthStore((s) => s.setSessionState);
  const setGetAccessTokenSilently = useAuthStore(
    (s) => s.setGetAccessTokenSilently,
  );

  useEffect(() => {
    setGetAccessTokenSilently(getAccessTokenSilently);
  }, [getAccessTokenSilently, setGetAccessTokenSilently]);

  useEffect(() => {
    let isCancelled = false;

    if (isLoading) {
      queueMicrotask(() => {
        if (!isCancelled) setSessionState(loadingState);
      });
      return () => {
        isCancelled = true;
      };
    }

    if (!isAuthenticated) {
      queueMicrotask(() => {
        if (!isCancelled) setSessionState(signedOutState);
      });
      return () => {
        isCancelled = true;
      };
    }

    async function loadSession() {
      queueMicrotask(() => {
        if (!isCancelled) setSessionState(loadingState);
      });

      try {
        const accessToken = await getAccessTokenSilently();
        const claims = decodeJwtClaims(accessToken);

        if (isCancelled) return;

        setSessionState({
          status: "authenticated",
          accessToken,
          claims,
          tenantIds: extractTenantIds(claims),
          roles: extractRoles(claims),
          error: null,
        });
      } catch (error) {
        if (isCancelled) return;

        setSessionState({
          status: "error",
          accessToken: null,
          claims: null,
          tenantIds: [],
          roles: [],
          error:
            error instanceof Error
              ? error.message
              : "The ClarityOS session could not be initialized.",
        });
      }
    }

    void loadSession();

    return () => {
      isCancelled = true;
    };
  }, [getAccessTokenSilently, isAuthenticated, isLoading, setSessionState]);
}
